import type {
  AccountCurrencyTypes,
  CustodianStatisticsHolding,
  Frequency,
  ManualServiceAccountTypes,
  Maybe,
  WorkflowActionTypes,
} from '@generated/graphql';
import type { BankAccount } from '@interfaces/bankAccount';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { TranslatedString } from '@interfaces/shared';
import type { Institution } from '@interfaces/transfer';
import type { PageObjectType } from '@ovComponents/5-page/pageConfiguration/pageConfigurationTypes';
import type { FormSignerMapping } from './subSteps/manageSigners/formSignerMapping';

export type CreatedAccountsType = {
  id?: string;
  type?: string;
  state?: string;
};

export const reconcileWorkflowAccountContext = (
  context: WorkflowDataType,
): WorkflowDataType => {
  if (!context.createdAccounts) return context;

  const remainingAccounts = context.createdAccounts ?? [];

  if (remainingAccounts.length === 0) {
    return {
      ...context,
      currentAccountId: undefined,
      currentAccountType: undefined,
      currentAccountState: undefined,
    };
  }

  const currentAccount = remainingAccounts.find(
    (account) => account.id === context.currentAccountId,
  );

  if (currentAccount) {
    if (
      currentAccount.type === context.currentAccountType &&
      currentAccount.state === context.currentAccountState
    ) {
      return context;
    }

    return {
      ...context,
      currentAccountType: currentAccount.type,
      currentAccountState: currentAccount.state,
    };
  }

  const lastAccount = remainingAccounts.at(-1)!;

  return {
    ...context,
    currentAccountId: lastAccount.id,
    currentAccountType: lastAccount.type,
    currentAccountState: lastAccount.state,
  };
};

export type TransferContextData = {
  temporaryTransferState?: 'completed' | 'failed';
  substepId?: string;
  temporaryId?: string;
  objectType?: PageObjectType;
  objectId?: string;
  type?: 'EFT' | 'INTERNAL_TRANSFER' | 'EXTERNAL_TRANSFER';
  scheduledTransferId?: string;
  bankAccount?: BankAccount;
  subAccount?: Partial<SubAccount>;
  fromSubAccount?: Partial<SubAccount>;
  institution?: Partial<Institution>;
  accountNumber?: string;
  accountType?: ManualServiceAccountTypes;
  fiscalYear?: number;
  transferType?: string;
  amountCents?: number;
  schedule?: Frequency;
  scheduledDate?: Date | string;
  skipDocuments?: boolean;
  currentTransferId?: string;
  holdings?: CustodianStatisticsHolding;
  participantNumber?: string;
  currency?: string;
  temporaryDeposits?: TransferContextData[];
  currentDepositTemporaryId?: string;
  referenceId?: string; // id of Transfer / ScheduledTransfer / ManualProcess objects that are created once submitted
};

export type WorkflowCustodianDepositTransfer = Partial<{
  confirmationNumber: string;
  accountId: string;
  amount: number;
  bankAccount: Partial<{
    accountNumber: string;
    institutionNumber: string;
    transitNumber: string;
  }>;
  frequency: string;
  fund: Maybe<string>;
  startDate: string | Date;
}>;

/**
 * Workflow handshake:
 *  1. DepositReview writes `input` to context and advances to the next step.
 *  2. SynchronousIntegration triggers a Windmill script that reads `input`,
 *     calls the custodian PAC API, and writes `response` back to context.
 *     action: complete on SUCCESS, redirect on CHALLENGE, or cancel on FAILURE.
 *
 * These types are not available via GraphQL codegen. They are a frontend-first
 * typed contract owned by us (FE + Windmill). The backend workflow context is
 * untyped (`any`). A follow-up ticket will formalize these in a
 * shared library across FE, BE, and Windmill.
 *
 * @see https://onevest.getoutline.com/doc/estimation-breakdown-6PmlFQrY9E
 */

/** Data needed by the Windmill script to call the custodian PAC API. */
export type CustodianDepositTransferContextInput = {
  /** session ID (from sessionStorage) for custodian risk engine. */
  cmid?: string;
  accountId?: string;
  organizationUserId?: string;
  amount?: number;
  /** CUSIP of the target financial product. */
  fund?: string;
  frequency?: string;
  startDate?: string;
  bankAccount?: {
    accountNumber?: string;
    institutionNumber?: string;
    transitNumber?: string;
  };
};

/** Response written by the Windmill script after calling the custodian PAC API. */
export type CustodianDepositTransferContextResponse = {
  status?: 'SUCCESS' | 'FAILURE' | 'CHALLENGE';
  transactionId?: string;
  confirmationNumber?: string;
  /** Present when the custodian risk engine requires additional verification. */
  action?: {
    type: string;
    details?: Array<{ key: string; value: unknown }>;
  };
  error?: {
    message: string;
    code: string;
  };
};

/**
 * Persisted on `WorkflowCompletion context custodianDepositTransfer`.
 * `input` is written by DepositReview; `response` is written by the Windmill script.
 * `challengeResult` is written by the RedirectHandler after the user returns from external MFA.
 */
export type CustodianDepositTransferContext = {
  input?: CustodianDepositTransferContextInput;
  response?: CustodianDepositTransferContextResponse;
  challengeResult?: {
    /**
     * SUCCESS/FAILURE are written on return from the external MFA page.
     * RETRYING is a transient marker set during handleRetry and overwritten
     * on the next MFA return — it is never the final persisted state.
     */
    status: 'SUCCESS' | 'FAILURE' | 'RETRYING';
    transactionId?: string;
    retryCount?: number;
    processedAt: string;
  };
};

export interface WorkflowDataType extends TransferContextData {
  // Users
  currentUserId?: string;

  // Accounts
  currentAccountId?: string;
  currentAccountType?: string;
  currentAccountState?: string;
  createdAccounts?: CreatedAccountsType[];
  skipEditAccount?: boolean;

  // Goals
  currentGoalId?: string;
  currentGoalType?: string;
  currentGoalName?: string;
  createdGoalIds?: string[];
  goalTimeHorizons?: any;
  goalRiskQuestions?: any;
  goalRiskQuestionTitle?: TranslatedString;

  // Sub-Accounts
  currentSubAccountId?: string;
  createdSubAccountIds?: string[];

  // Households
  currentHouseholdClientGroupId?: string;
  linkingHouseholdId?: string;
  isHouseholdRemoved?: boolean;

  // Transfers - Deposit or Withdrawal
  createdTransfers?: {
    id: string;
  }[];
  createdScheduledTransfers?: {
    id: string;
  }[];
  createdExternalTransfers?: {
    id: string;
    subAccount: {
      id: string;
      account: { id: string };
      transfers: { id: string; manualProcess: { id: string } }[];
    };
  }[];
  createdCustodianDepositTransfers?: WorkflowCustodianDepositTransfer[];
  custodianDepositTransfer?: CustodianDepositTransferContext;

  // Model Portfolios
  modelPortfolioDetails?: any;

  // Section Navigation
  segmentHistory?: string[];
  currentSegment?: string | null;

  // Other
  childWorkflowCompletion?: any;

  // Signing
  signerMapping?: FormSignerMapping[];
  integrationFormPackId?: string;
  formMapping?: {
    formDefinitionKey: string;
    objectId: string;
    objectType: string;
  }[];

  userAllowedNumberOfAttempts?: any;
  usedVerificationAttempts?: any;
  isUserVerified?: any;

  // Net Worth
  netWorthData?: {
    externalSourceType?: string;
    netWorthAccountNickname?: Partial<TranslatedString>;
    netWorthAmountCents?: number;
    publicToken?: string;
    netWorthLinking?: 'MANUAL' | 'AUTOMATIC';
    netWorthStep?: number;
    netWorthLinkingActionType?: WorkflowActionTypes;
    netWorthLinkingNotificationGroup?: string;
    netWorthCurrency?: AccountCurrencyTypes;
    notificationSuccessTitle?: string;
    notificationSuccessBody?: string;
    notificationFailureTitle?: string;
    notificationFailureBody?: string;
    notificationLinkText?: string;
    notificationLink?: string;
  };
}

export const toTransferContextData = (
  workflowContext: WorkflowDataType,
): TransferContextData => ({
  substepId: workflowContext.substepId,
  temporaryId: workflowContext.temporaryId,
  objectType: workflowContext.objectType,
  objectId: workflowContext.objectId,
  type: workflowContext.type,
  bankAccount: workflowContext.bankAccount,
  subAccount: workflowContext.subAccount,
  fromSubAccount: workflowContext.fromSubAccount,
  institution: workflowContext.institution,
  accountNumber: workflowContext.accountNumber,
  accountType: workflowContext.accountType,
  fiscalYear: workflowContext.fiscalYear,
  transferType: workflowContext.transferType,
  amountCents: workflowContext.amountCents,
  schedule: workflowContext.schedule,
  scheduledDate: workflowContext.scheduledDate,
  skipDocuments: workflowContext.skipDocuments,
  currentTransferId: workflowContext.currentTransferId,
  holdings: workflowContext.holdings,
  participantNumber: workflowContext.participantNumber,
});

export const CREATED_OBJECT_FIELDS = [
  'createdAccounts',
  'createdGoalIds',
  'createdSubAccountIds',
  'createdTransfers',
  'createdScheduledTransfers',
  'createdExternalTransfers',
  'createdCustodianDepositTransfers',
] as const satisfies ReadonlyArray<keyof WorkflowDataType>;

export const buildClearedCreatedFields = (): Pick<
  WorkflowDataType,
  (typeof CREATED_OBJECT_FIELDS)[number]
> =>
  Object.fromEntries(CREATED_OBJECT_FIELDS.map((key) => [key, []])) as Pick<
    WorkflowDataType,
    (typeof CREATED_OBJECT_FIELDS)[number]
  >;
